import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { employmentService } from '../../services/api';
import { usePermissions } from '../PermissionsContext';
import { useConfirm } from '../Common/ConfirmDialog';
import { useToast } from '../Common/ToastProvider';
import AnnexDialog from './AnnexDialog';
import { annexTypeLabel, apiErrorText, fmtDate, fmtMoney, isoToday } from './salaryFormat';

// S3 — Employee File → "Contract & Salary" tab: contracted salary today, the next scheduled
// change, the labour contract, and every annex with its document in the dossier.
function SummaryCard({ title, value, lines, warn }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%', borderColor: warn ? 'warning.main' : 'divider' }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.05em' }}>{title}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, my: 0.5, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {lines.filter(Boolean).map((l, i) => (
        <Typography key={i} variant="body2" color="text.secondary" component="div">{l}</Typography>
      ))}
    </Paper>
  );
}

// Gross and net in separate columns; the value written in the document is bold, the other is calculated.
function MoneyCell({ salary, basis }) {
  if (!salary) return <TableCell align="right">—</TableCell>;
  const fromDocument = salary.basis === basis;
  return (
    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: fromDocument ? 600 : 400, whiteSpace: 'nowrap' }}>
      {fmtMoney(basis === 'Gross' ? salary.gross : salary.net, salary.currency)}
    </TableCell>
  );
}

export default function ContractSalaryTab({ personId }) {
  const { hasPermission } = usePermissions();
  const confirm = useConfirm();
  const toast = useToast();
  const canManage = hasPermission('ManageContracts');

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [annexDlg, setAnnexDlg] = React.useState({ open: false, annex: null });
  const [signDlg, setSignDlg] = React.useState({ open: false, annex: null, signedDate: isoToday(), documentPath: '' });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await employmentService.getSummary(personId));
    } catch (err) {
      setData(null);
      setError(err?.response?.status === 403
        ? 'You do not have permission to view contracts and salaries.'
        : apiErrorText(err, 'Could not load the contract and salary.'));
    } finally {
      setLoading(false);
    }
  }, [personId]);

  React.useEffect(() => { load(); }, [load]);

  const deleteAnnex = async (annex) => {
    if (!(await confirm({ message: `Delete the draft annex ${annex.annexNumber || ''}?`, danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await employmentService.deleteAnnex(annex.id);
      toast('Draft annex deleted', { variant: 'success' });
      load();
    } catch (err) { toast(apiErrorText(err, 'Could not delete the annex.'), { variant: 'error' }); }
  };

  const signAnnex = async () => {
    try {
      await employmentService.signAnnex(signDlg.annex.id, { signedDate: signDlg.signedDate, documentPath: signDlg.documentPath || null });
      toast('Annex marked as signed', { variant: 'success' });
      setSignDlg((s) => ({ ...s, open: false }));
      load();
    } catch (err) { toast(apiErrorText(err, 'Could not mark the annex as signed.'), { variant: 'error' }); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity={error.startsWith('You do not') ? 'info' : 'error'}>{error}</Alert>;
  if (!data) return null;

  const c = data.contract;
  const cur = data.currentSalary;
  const next = data.scheduledChange;
  const alerts = (data.checks || []).filter((k) => k.code === 'DRAFT_PAST_EFFECTIVE' || k.code === 'NO_CONTRACT');

  return (
    <Box>
      {!c ? (
        <Alert severity="warning" sx={{ mb: 2 }}>This person has no labour contract in HRApp. Add it in the General tab.</Alert>
      ) : (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <SummaryCard title="Contracted salary today"
              value={`${fmtMoney(cur.salary.gross, cur.salary.currency)} gross`}
              lines={[<b key="n">{fmtMoney(cur.salary.net, cur.salary.currency)} net</b>,
                `Since ${fmtDate(cur.since)} · ${cur.source} · ${cur.salary.basis.toLowerCase()} in document`]} />
          </Grid>
          <Grid item xs={12} md={4}>
            {next?.salary ? (
              <SummaryCard warn={next.status === 'Draft'} title="Scheduled change"
                value={`${fmtMoney(next.salary.gross, next.salary.currency)} gross`}
                lines={[<b key="n">{fmtMoney(next.salary.net, next.salary.currency)} net</b>,
                  <span key="a">From {fmtDate(next.effectiveDate)} · Annex {next.annexNumber || ''} · {next.salary.basis.toLowerCase()} in document{' '}
                  <Chip size="small" label={next.status === 'Draft' ? 'Draft – not signed' : 'Signed'} color={next.status === 'Draft' ? 'warning' : 'success'} /></span>]} />
            ) : (
              <SummaryCard title="Scheduled change" value="None" lines={['No annex with a later effective date']} />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryCard title="Labour contract" value={`No. ${c.contractNumber}`}
              lines={[`Signed ${fmtDate(c.contractDate)} · start ${fmtDate(c.startDate)}`,
                `${c.contractTerm} · ${c.annualLeaveDaysPerYear} days leave`]} />
          </Grid>
        </Grid>
      )}

      {alerts.map((k) => <Alert key={k.code + k.text} severity="warning" sx={{ mb: 1 }}>{k.text}</Alert>)}

      {c && (
        <>
          <Stack direction="row" alignItems="center" sx={{ mb: 1, mt: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Contract and annexes</Typography>
            {canManage && <Button variant="contained" onClick={() => setAnnexDlg({ open: true, annex: null })}>+ Add annex</Button>}
          </Stack>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Document</TableCell><TableCell>Type</TableCell><TableCell>Signed</TableCell>
                  <TableCell>Effective</TableCell><TableCell align="right">Gross</TableCell><TableCell align="right">Net</TableCell><TableCell>In document</TableCell>
                  <TableCell>Status</TableCell><TableCell>File in dossier</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.documents.map((a) => (
                  <TableRow key={a.id} sx={a.status === 'Draft' ? { bgcolor: 'action.hover' } : undefined}>
                    <TableCell>{a.annexNumber ? `Annex ${a.annexNumber}` : 'Annex'}</TableCell>
                    <TableCell>{annexTypeLabel(a.type)}{a.newPositionTitle ? ` → ${a.newPositionTitle}` : ''}</TableCell>
                    <TableCell>{fmtDate(a.signedDate)}</TableCell>
                    <TableCell>{fmtDate(a.effectiveDate)}</TableCell>
                    <MoneyCell salary={a.salary} basis="Gross" />
                    <MoneyCell salary={a.salary} basis="Net" />
                    <TableCell>{a.salary?.basis || '—'}</TableCell>
                    <TableCell><Chip size="small" label={a.status} color={a.status === 'Draft' ? 'warning' : 'success'} /></TableCell>
                    <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.documentPath || ''}>{a.documentPath || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {canManage && (
                        <Stack direction="row" spacing={0.5}>
                          {a.status === 'Draft' && <Button onClick={() => setSignDlg({ open: true, annex: a, signedDate: isoToday(), documentPath: a.documentPath || '' })}>Mark signed</Button>}
                          <Button onClick={() => setAnnexDlg({ open: true, annex: a })}>Edit</Button>
                          {a.status === 'Draft' && <Button color="error" onClick={() => deleteAnnex(a)}>Delete</Button>}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>Contract {c.contractNumber}</TableCell>
                  <TableCell>Labour contract</TableCell>
                  <TableCell>{fmtDate(c.contractDate)}</TableCell>
                  <TableCell>{fmtDate(c.startDate)}</TableCell>
                  <MoneyCell salary={c.salary} basis="Gross" />
                  <MoneyCell salary={c.salary} basis="Net" />
                  <TableCell>{c.salary.basis}</TableCell>
                  <TableCell><Chip size="small" label="Signed" color="success" /></TableCell>
                  <TableCell>{c.hasFile ? 'Contract file' : '—'}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The amount written in each document is in bold; the other one is calculated with the employee contribution and income-tax rates
            and the maximum insurable income of the year (base salary only). A draft annex never changes the contracted salary.
          </Typography>
        </>
      )}

      <AnnexDialog open={annexDlg.open} contractId={c?.contractId} annex={annexDlg.annex}
        onClose={() => setAnnexDlg({ open: false, annex: null })}
        onSaved={() => { setAnnexDlg({ open: false, annex: null }); load(); }} />

      <Dialog open={signDlg.open} onClose={() => setSignDlg((s) => ({ ...s, open: false }))} fullWidth maxWidth="xs">
        <DialogTitle>Mark annex as signed</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField id="sign-date" type="date" label="Signed on" InputLabelProps={{ shrink: true }}
              value={signDlg.signedDate} onChange={(e) => setSignDlg((s) => ({ ...s, signedDate: e.target.value }))} />
            <TextField id="sign-path" label="Signed annex in dossier (path)" value={signDlg.documentPath}
              onChange={(e) => setSignDlg((s) => ({ ...s, documentPath: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignDlg((s) => ({ ...s, open: false }))}>Cancel</Button>
          <Button variant="contained" disabled={!signDlg.signedDate} onClick={signAnnex}>Mark signed</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
